/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/display-name */
/**
 * Component: Live View
 * Date: 1/12/2021
 * */

import React, { useLayoutEffect, useRef } from 'react';
import { Table, Tag, Tooltip } from 'components/common';
import mixpanel from 'utils/mixpanel';
import moment from 'moment';
import { LiveDataContext } from 'context/liveData';
import { substringAfterOccurance } from 'utils/commonFunctions';
import styles from './style.module.css';

/**
 * Overlay component to update text on hover.
 */
const Overlay = ({ text }: any) => {
  return <span>{`last updated ${moment(text).fromNow()}`}</span>;
};

function Live(): JSX.Element {
  const { liveData } = LiveDataContext();
  const maxScrollPercent = useRef(0);
  /**
   * Enums for agent Status, ONLINE OFFLINE ISSUES
   * */
  const getAgentStatusType = (tag: string) => {
    switch (tag) {
      case 'ONLINE':
        return { key: 'statusOnline', text: 'Online' };
      case 'OFFLINE':
        return { key: 'statusOffline', text: 'Offline' };
      case 'AUDIO_ISSUE':
        return { key: 'statusIssue', text: 'Audio issues' };
      case 'AGENT_AWAY':
        return {
          key: 'statusError',
          text: 'Agent taking calls but not on Prodigal',
        };
      default:
        return { key: tag, text: tag };
    }
  };

  /**
   *  Call Status Enums.
   *  */
  const getCallStatusType = (callStatus: string) => {
    switch (callStatus) {
      case 'IN_CALL':
        return 'In Call';
      case 'NOTES_FAILED':
        return 'Notes taking failed';
      case 'GENERATING_NOTES':
        return 'Generating a note';
      case 'CALL_START_WAITING':
        return 'Waiting for Call to Start';
      case 'CALL_START_WAITING_WO_NOTES':
        return 'Waiting for Call to Start';
      case 'EDITING_NOTES':
        return 'Editing a note';
      default:
        return callStatus;
    }
  };

  /**
   * Alphabetical compare utility for strings.
   * @param a {string} string 1 to compare.
   * @param b {string} string 2 to compare with string 2.
   */
  const lexicalComparor = (a: string, b: string) => {
    try {
      return a.localeCompare(b);
    } catch {
      if (a === b) {
        return 0;
      }

      if (a > b) {
        return 1;
      }

      return -1;
    }
  };

  /**
   * Columns object for antd Table.
   */
  const columns = [
    {
      title: 'ID',
      dataIndex: 'agentLoginId',
      key: 'agentLoginId',
      render: (text: any) => (
        <span className={styles.rowListId}>
          {substringAfterOccurance(`${text}`, '-')}
        </span>
      ),
      sorter: (a: any, b: any) =>
        lexicalComparor(a.agentLoginId, b.agentLoginId),
    },
    {
      title: 'First Name',
      dataIndex: 'agentFirstname',
      key: 'agentFirstname',
      render: (text: any) => <span className={styles.firstName}>{text}</span>,
      sorter: (a: any, b: any) =>
        lexicalComparor(a.agentFirstname, b.agentFirstname),
    },
    {
      title: 'Last Name',
      dataIndex: 'agentLastname',
      key: 'agentLastname',
      render: (text: any) => <span className={styles.lastName}>{text}</span>,
      sorter: (a: any, b: any) =>
        lexicalComparor(a.agentLastname, b.agentLastname),
    },
    {
      title: 'Agent Status',
      dataIndex: 'callStatus',
      key: 'callStatus',
      render: (textStr: string, record: any) => {
        return (
          <Tooltip
            destroyTooltipOnHide={{ keepParent: false }}
            placement="left"
            overlay={<Overlay text={record.lastUpdatedCallStatus} />}
          >
            <span className={styles.subStatusProNotes}>
              {getCallStatusType(textStr)}
            </span>
          </Tooltip>
        );
      },
      sorter: (a: any, b: any) =>
        lexicalComparor(
          getCallStatusType(a.callStatus),
          getCallStatusType(b.callStatus),
        ),
    },
    {
      title: 'Prodigal Status',
      key: 'status',
      dataIndex: 'status',
      width: '28%',
      maxWidth: '358px',
      render: (tag: any, record: any) => (
        <Tooltip
          destroyTooltipOnHide={{ keepParent: false }}
          placement="left"
          overlay={<Overlay text={record.lastUpdatedProNotesStatus} />}
        >
          <div className={styles.cursor_pointer}>
            <Tag key={tag} type={getAgentStatusType(tag).key}>
              {getAgentStatusType(tag).text}
            </Tag>
          </div>
        </Tooltip>
      ),
      sorter: (a: any, b: any) =>
        lexicalComparor(
          getAgentStatusType(a.status).text,
          getAgentStatusType(b.status).text,
        ),
    },
  ];

  /**
   * reset the the max scroll counter and trigger an event for mixpanel
   * on having items in the list of note history.
   */
  const resetScrollMax = (obj: any) => {
    if (obj) {
      // Mixpanel event for scoll length
      mixpanel('Max Scroll', 'Live View', {
        userId: `${localStorage.getItem('userEmail')}`,
        maxPercentage: `${maxScrollPercent.current}`,
      });
    }
    maxScrollPercent.current = 0;
  };

  useLayoutEffect(() => {
    const obj: any = document.getElementsByClassName(
      'ant-table-body',
    )[0] as any;
    if (obj) {
      obj.onscroll = () => {
        try {
          const height = obj.clientHeight;
          const scrollHeight = obj.scrollHeight - height;
          const { scrollTop } = obj;
          const percent = Math.floor((scrollTop / scrollHeight) * 100);
          maxScrollPercent.current = percent;
        } catch {
          // catch and ignore errors.
        }
      };
    }

    // reset and update mixpanel on unmount.
    return () => {
      resetScrollMax(obj);
    };
  }, []);

  return (
    <div className={styles.liveContainer}>
      <Table
        columns={columns}
        dataSource={liveData}
        pagination={false}
        scroll={{ y: '83vh' }}
      />
    </div>
  );
}

export default Live;
